/**
 * Push subscription manager.
 * Handles browser push notification permission, subscription, and unsubscription.
 * Feature-detects PushManager, Notification API, and ServiceWorker support.
 */
import api from '../services/api';

/**
 * Check if push notifications are supported in the current browser.
 * @returns true if all required APIs are available
 */
export function isPushSupported(): boolean {
  return (
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  );
}

/**
 * Permission state, widened with 'unsupported'.
 *
 * ONBOARD-FIX-1 A2.3: this used to be a bare NotificationPermission, and a browser with
 * no Notification API was reported as 'denied'. "This browser can't do notifications" and
 * "you blocked notifications" are different facts and must not share a UI state.
 */
export type PermissionState = NotificationPermission | 'unsupported';

/**
 * Request notification permission from the user.
 *
 * ONBOARD-FIX-1 A2.3 — two real-world cases used to be mis-read as a denial:
 *  - Chrome/Edge's QUIET permission UI resolves the promise with 'default' immediately and
 *    lets the user decide later via the address-bar bell.
 *  - Safari <= 15 (desktop) implements ONLY the legacy callback form, so the call returns
 *    undefined and `await undefined` yields undefined.
 * Both are non-answers, not blocks. So we accept either calling convention and then
 * RE-READ `Notification.permission`, which is authoritative, preferring a live 'granted'
 * or 'denied' over whatever the call itself reported.
 *
 * MUST stay the first thing reached from the click handler — Firefox and Safari require
 * transient user activation, so do not `await` anything before calling this.
 *
 * @returns 'granted' | 'denied' | 'default' | 'unsupported'
 */
export async function requestNotificationPermission(): Promise<PermissionState> {
  if (!('Notification' in window)) {
    return 'unsupported';
  }

  try {
    const returned = await new Promise<NotificationPermission | undefined>((resolve) => {
      let settled = false;
      const done = (p?: NotificationPermission): void => {
        if (!settled) {
          settled = true;
          resolve(p);
        }
      };
      // Passing the legacy callback is still permitted in browsers that also return a
      // promise, so one call covers both conventions.
      const maybePromise = Notification.requestPermission(done) as
        | Promise<NotificationPermission>
        | undefined;
      if (maybePromise && typeof maybePromise.then === 'function') {
        maybePromise.then(done, () => done(undefined));
      }
    });

    const live = Notification.permission;
    if (returned === 'granted' || live === 'granted') return 'granted';
    if (returned === 'denied' || live === 'denied') return 'denied';
    return 'default';
  } catch {
    // A throw here is not a denial — fall back to whatever the browser currently holds.
    return Notification.permission === 'granted' ? 'granted' : 'default';
  }
}

/**
 * Get the current notification permission status.
 * @returns Current permission state ('unsupported' when the API is absent)
 */
export function getPermissionStatus(): PermissionState {
  if (!('Notification' in window)) {
    return 'unsupported';
  }
  return Notification.permission;
}

/**
 * Why a subscribe attempt failed. Each one is a genuinely different situation and
 * the UI must not describe any of them as "permission denied".
 *  - 'unsupported' — the browser lacks ServiceWorker / PushManager / Notification.
 *  - 'sw'          — the service worker never became ready (see the timeout note below).
 *  - 'vapid'       — the server's VAPID public key is missing or malformed (atob throws).
 *  - 'subscribe'   — pushManager.subscribe() rejected (push service refused).
 *  - 'server'      — we got a subscription but could not hand it to our backend.
 */
export type SubscribeFailureReason = 'unsupported' | 'sw' | 'vapid' | 'subscribe' | 'server';

/**
 * Deliberately ONE object type rather than a discriminated union: this project compiles
 * with `strict: false`, so `{ok:true} | {ok:false, reason}` does not narrow on `result.ok`
 * and every `.reason` read becomes a type error at the call sites.
 */
export interface SubscribeResult {
  ok: boolean;
  /** Present only when ok is false. */
  reason?: SubscribeFailureReason;
  error?: unknown;
}

/**
 * `navigator.serviceWorker.ready` never rejects — it simply hangs forever when no worker
 * activates (private windows with SW disabled, blocked storage, a failed /sw.js fetch, an
 * enterprise proxy, and always under `npm run dev`, where main.tsx unregisters workers).
 * Without this cap the caller's spinner sticks on "Enabling…" with no toast, permanently.
 */
const SW_READY_TIMEOUT_MS = 10_000;

async function serviceWorkerReady(): Promise<ServiceWorkerRegistration> {
  return Promise.race([
    navigator.serviceWorker.ready,
    new Promise<never>((_, reject) =>
      setTimeout(() => reject(new Error('Service worker not ready')), SW_READY_TIMEOUT_MS),
    ),
  ]);
}

/**
 * Subscribe the current browser to push notifications.
 * Registers with the push service and sends the subscription to the backend.
 *
 * ONBOARD-FIX-1 A2.3: this used to return a bare boolean from a single try/catch, so a
 * dead service worker, a bad VAPID key, a refused subscribe and a 500 from our own API
 * were indistinguishable — all of them surfaced as one generic red toast that users read
 * as "notifications are broken/denied". Each step now reports itself.
 *
 * @param vapidPublicKey - VAPID public key from the server
 * @returns ok, or the specific reason it failed
 */
export async function subscribeToPush(vapidPublicKey: string): Promise<SubscribeResult> {
  if (!isPushSupported()) {
    console.warn('Push notifications not supported');
    return { ok: false, reason: 'unsupported' };
  }

  let registration: ServiceWorkerRegistration;
  try {
    registration = await serviceWorkerReady();
  } catch (error) {
    console.error('Push: service worker never became ready', error);
    return { ok: false, reason: 'sw', error };
  }

  // An existing browser subscription still has to be re-sent: the server may never have
  // received it (a prior attempt that died at the POST). This is the only repair path.
  try {
    const existingSub = await registration.pushManager.getSubscription();
    if (existingSub) {
      try {
        await sendSubscriptionToBackend(existingSub);
        return { ok: true };
      } catch (error) {
        console.error('Push: failed to register existing subscription with the server', error);
        return { ok: false, reason: 'server', error };
      }
    }
  } catch (error) {
    console.error('Push: could not read the existing subscription', error);
    return { ok: false, reason: 'subscribe', error };
  }

  let applicationServerKey: Uint8Array;
  try {
    applicationServerKey = urlBase64ToUint8Array(vapidPublicKey);
  } catch (error) {
    console.error('Push: malformed VAPID public key', error);
    return { ok: false, reason: 'vapid', error };
  }

  let subscription: PushSubscription;
  try {
    subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: applicationServerKey.buffer as ArrayBuffer,
    });
  } catch (error) {
    console.error('Push: pushManager.subscribe() rejected', error);
    return { ok: false, reason: 'subscribe', error };
  }

  try {
    await sendSubscriptionToBackend(subscription);
  } catch (error) {
    console.error('Push: failed to send subscription to the server', error);
    return { ok: false, reason: 'server', error };
  }

  return { ok: true };
}

/**
 * Unsubscribe the current browser from push notifications.
 * Removes the subscription from both the browser and the backend.
 * @returns true if unsubscribed successfully
 */
export async function unsubscribeFromPush(): Promise<boolean> {
  if (!isPushSupported()) return false;

  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();

    if (!subscription) {
      return true; // Already unsubscribed
    }

    // Notify backend
    await api.post('/notifications/push/unsubscribe', {
      endpoint: subscription.endpoint,
    });

    // Unsubscribe from browser
    await subscription.unsubscribe();
    return true;
  } catch (error) {
    console.error('Failed to unsubscribe from push:', error);
    return false;
  }
}

/**
 * Send a push subscription to the backend for storage.
 * @param subscription - Browser PushSubscription object
 */
async function sendSubscriptionToBackend(subscription: PushSubscription): Promise<void> {
  const key = subscription.getKey('p256dh');
  const auth = subscription.getKey('auth');

  if (!key || !auth) {
    throw new Error('Push subscription missing keys');
  }

  await api.post('/notifications/push/subscribe', {
    subscription: {
      endpoint: subscription.endpoint,
      keys: {
        p256dh: arrayBufferToBase64(key),
        auth: arrayBufferToBase64(auth),
      },
    },
    deviceType: detectDeviceType(),
    // The server caps this at 500 chars (subscribePushSchema) and 400s past it; truncate
    // here rather than let a long UA turn into an opaque "couldn't enable" failure.
    userAgent: navigator.userAgent.slice(0, 500),
  });
}

/**
 * Convert a URL-safe base64 string to Uint8Array (for VAPID key).
 * @param base64String - URL-safe base64 string
 * @returns Uint8Array
 */
function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; i++) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

/**
 * Convert ArrayBuffer to base64 string.
 * @param buffer - ArrayBuffer from subscription key
 * @returns Base64 string
 */
function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

/**
 * Detect device type from user agent.
 * @returns Device type string
 */
function detectDeviceType(): string {
  const ua = navigator.userAgent.toLowerCase();
  if (/iphone|ipad|ipod/.test(ua)) return 'ios';
  if (/android/.test(ua)) return 'android';
  if (/windows/.test(ua)) return 'windows';
  if (/macintosh|mac os/.test(ua)) return 'macos';
  if (/linux/.test(ua)) return 'linux';
  return 'unknown';
}